import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, Ref, watch } from "vue";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import { useTranslations } from "@/hooks/useTranslations";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import VCautionaryInputs from "@/components/vCautionaryInputs.vue";
import { LabelTypeMetadata } from "@/models/NutritionLabels/LabelTypeMetadata";
import { CautionarySideOfPackRenderData, ExportLabelState } from "@/models/NutritionLabels/ExportLabelState";
import { LabelLanguage } from "@/models/NutritionLabels/LabelLanguage";
import { vSelectOptionBase } from "@/typings/common";
import { LabelTypes } from "@/models/NutritionLabels/LabelTypes";
import { parseCountryKey } from "@/models/NutritionLabels/v2/Helpers";
import { VPopover } from "@/components/v-tooltip";

type LabelTypeOption = vSelectOptionBase<string> & {
  locked?: boolean;
};

type LanguageOption = {
  name: string;
  value: string;
  disabled?: boolean;
};

type LabelTypeSelectionProps = {
  value: ExportLabelState;
  showLanguageSelection?: boolean;
  recipeId: number;
  country: string;
  initialLabelTemplate: string;
  initialLanguage: string;
  labelTypeFilter?: LabelTypeMetadata;
  isTrial?: boolean;
  restrictTrialToDefaultOnly?: boolean;
  trialAvailabilityMessage?: string;
  openSubscriptionInNewTab?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'LabelTypeSelection',
  props: {
    value: null,
    showLanguageSelection: { type: Boolean, default: true },
    recipeId: null,
    country: null,
    initialLabelTemplate: { default: "" },
    initialLanguage: { default: "" },
    labelTypeFilter: { default: null },
    isTrial: { type: Boolean, default: false },
    restrictTrialToDefaultOnly: { type: Boolean, default: false },
    trialAvailabilityMessage: { default: "Free Trial includes Standard Vertical and Supplement labels." },
    openSubscriptionInNewTab: { type: Boolean, default: false }
  },
  emits: [
  "update:value",
  "reset-model",
  "update:selectedLabelType",
  "update:primaryLanguage",
  "update:freeTextFields",
  "cautionaryState"
],
  setup(__props: any, { emit }) {

const props = __props as { value: ExportLabelState, showLanguageSelection: boolean, recipeId: number, country: string, initialLabelTemplate: string, initialLanguage: string, labelTypeFilter: LabelTypeMetadata, isTrial: boolean, restrictTrialToDefaultOnly: boolean, trialAvailabilityMessage: string, openSubscriptionInNewTab: boolean };

const TRIAL_ALLOWED_LABEL_KEYS = [
  "StandardVerticalLabel",
  "SupplementLabel",
];



const trialAvailabilityMessage = computed(() => props.trialAvailabilityMessage);



const translations = useTranslations();
const localModel = ref(props.value);

watch(localModel, (val) => emit("update:value", val), { deep: true });

watch(
  () => props.initialLabelTemplate,
  (newValue) => {
    const nextLabelKey = getInitialLabelKey(newValue);

    if (nextLabelKey) {
      syncSelectedLabelType(nextLabelKey);
    }
  }
);

const selectedLabelType: Ref<LabelTypeOption | null> = ref(null);
const selectedLanguage = ref<LanguageOption | null>(null);
const allLabelsData = ref<Record<string, any>>({});
const labelTypes = ref<string[]>([]);
const supportedLanguagesOptions = ref<LanguageOption[]>([]);

watch(
  () => props.initialLanguage,
  (val) => {
    selectedLanguage.value = supportedLanguagesOptions.value.find((lang) => lang.value === val) || null;
  }
);

const labelTypeOptions = computed(() => {
  const rv: LabelTypeOption[] = [];

  for (const labelKey of labelTypes.value) {
    rv.push({
      name: translations.value.get(labelKey),
      value: labelKey,
      locked: isLockedLabelType(labelKey),
    });
  }
  return rv;
});

const shouldDisplaySupplementFoodTypeOptions = computed(() => {
  return (
    selectedLabelType.value
    && selectedLabelType.value.value == LabelTypes[LabelTypes.SupplementLabel]
    && props.country === 'Canada'
  );
});

const languageOptionsForTrial = computed(() => {
  if (!props.isTrial) {
    return supportedLanguagesOptions.value;
  }

  return supportedLanguagesOptions.value.map((lang) => ({
    ...lang,
    disabled: lang.value !== "English",
  }));
});

onMounted(fetchLabels);

async function fetchLabels() {
  try {
    const response = await NutritionLabelRepository.listLabelsV2(props.labelTypeFilter);
    allLabelsData.value = response.data;

    const countryKey = parseCountryKey(props.country);
    const countryLabels = allLabelsData.value[countryKey] || {};
    labelTypes.value = Object.keys(countryLabels);

    if (labelTypes.value.length > 0) {
      const defaultLabelKey = getInitialLabelKey(props.initialLabelTemplate);

      if (defaultLabelKey) {
        syncSelectedLabelType(defaultLabelKey);
      }
    }
  }
  catch (err) {
    selectedLabelType.value = null;
  }
}

function updateFilters(selected: LabelTypeOption) {
  if (!selected || isLockedLabelType(selected.value)) {
    return;
  }

  selectedLabelType.value = selected;
  emit("update:selectedLabelType", selected);
  updateLanguagesForLabel(selected.value);
  emit("reset-model");
}

function getTrialAllowedLabelKeys() {
  if (props.restrictTrialToDefaultOnly) {
    return labelTypes.value.slice(0, 1);
  }

  const allowedLabelKeys = labelTypes.value.filter((labelKey) => TRIAL_ALLOWED_LABEL_KEYS.includes(labelKey));

  return allowedLabelKeys.length > 0 ? allowedLabelKeys : labelTypes.value.slice(0, 1);
}

function isLockedLabelType(labelKey: string) {
  return !!props.isTrial && !getTrialAllowedLabelKeys().includes(labelKey);
}

function getInitialLabelKey(requestedLabelKey?: string) {
  if (props.isTrial) {
    const trialAllowedLabelKeys = getTrialAllowedLabelKeys();

    if (requestedLabelKey && trialAllowedLabelKeys.includes(requestedLabelKey)) {
      return requestedLabelKey;
    }

    return trialAllowedLabelKeys[0];
  }

  if (requestedLabelKey) {
    return labelTypes.value.find((key) => key === requestedLabelKey) || labelTypes.value[0];
  }

  return labelTypes.value[0];
}

function syncSelectedLabelType(labelKey: string) {
  const nextSelection = labelTypeOptions.value.find((option) => option.value === labelKey);

  if (!nextSelection) {
    return;
  }

  selectedLabelType.value = nextSelection;
  emit("update:selectedLabelType", nextSelection);
  updateLanguagesForLabel(nextSelection.value);
}

function handleOptionPointerDown(option: LabelTypeOption, event: MouseEvent) {
  if (!option.locked) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

function handleLanguageOptionPointerDown(option: LanguageOption, event: MouseEvent) {
  if (!option.disabled) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
}

function updateLanguagesForLabel(labelKey: string) {
  const countryKey = parseCountryKey(props.country);
  const countryLabels = allLabelsData.value[countryKey] || {};
  const languages = countryLabels[labelKey] || [];

  supportedLanguagesOptions.value = languages.map((lang: string) => ({
    name: translations.value.get(lang),
    value: lang,
    disabled: props.isTrial && lang !== "English",
  }));

  if (supportedLanguagesOptions.value.length > 0) {
    const enabledLanguages = supportedLanguagesOptions.value.filter((lang) => !lang.disabled);
    const defaultLanguage = enabledLanguages.find(
      (lang) => lang.value == String(props.initialLanguage || "")
    ) || enabledLanguages[0];

    selectedLanguage.value = defaultLanguage || supportedLanguagesOptions.value[0];

    emit("update:primaryLanguage", selectedLanguage.value.value);
  }
}

function emitLanguageChange(selectedLang: LanguageOption) {
  emit("update:primaryLanguage", selectedLang.value);
}

function handleCautionaryModelUpdate(updatedModel: CautionarySideOfPackRenderData) {
  localModel.value.labelTypes[LabelTypeMetadata.CautionarySideOfPack] = updatedModel;
}

function handleCautionaryQualification(state: boolean) {
  emit("cautionaryState", state);
}

return { __sfc: true,TRIAL_ALLOWED_LABEL_KEYS, props, trialAvailabilityMessage, emit, translations, localModel, selectedLabelType, selectedLanguage, allLabelsData, labelTypes, supportedLanguagesOptions, labelTypeOptions, shouldDisplaySupplementFoodTypeOptions, languageOptionsForTrial, fetchLabels, updateFilters, getTrialAllowedLabelKeys, isLockedLabelType, getInitialLabelKey, syncSelectedLabelType, handleOptionPointerDown, handleLanguageOptionPointerDown, updateLanguagesForLabel, emitLanguageChange, handleCautionaryModelUpdate, handleCautionaryQualification, CollapsableCard, VCautionaryInputs, LabelTypeMetadata, LabelLanguage, VPopover }
}

})